import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  MentoringProgramGuide,
  MentoringProgramGuideRequirementType,
  MentoringProgramMilestone,
} from 'redux/schemas/models/mentoring-program-guides';


type GuidesParams = {
  mentoringProgramId: number;
  guideId?: number;
  mentorshipProgramGuide?: Partial<MentoringProgramGuide>;
  type?: MentoringProgramGuideRequirementType;
  state?: MentoringProgramMilestone;
  sequence?: number;
};

export const getMentorshipProgramGuides = createAsyncThunk<MentoringProgramGuide[], GuidesParams>(
  'GET_MENTORSHIP_PROGRAM_GUIDES',
  async ({ mentoringProgramId, type, state, sequence }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/mentorship_programs/${mentoringProgramId}/guides.json`,
        {
          params: {
            type,
            state,
            sequence,
          },
        },
      );

      return response.data.result as MentoringProgramGuide[];
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const getMentorshipProgramGuide = createAsyncThunk<MentoringProgramGuide, GuidesParams>(
  'GET_MENTORSHIP_PROGRAM_GUIDE',
  async ({ mentoringProgramId, guideId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/mentorship_programs/${mentoringProgramId}/guides/${guideId}.json`,
      );

      return response.data.result as MentoringProgramGuide;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));

      return rejectWithValue(error);
    }
  },
);


export const createMentorshipProgramGuide = createAsyncThunk<MentoringProgramGuide, GuidesParams>(
  'CREATE_MENTORSHIP_PROGRAM_GUIDE',
  async ({ mentoringProgramId, mentorshipProgramGuide }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/mentorship_programs/${mentoringProgramId}/guides.json`,
        {
          mentorshipProgramGuide,
        },
      );

      return response.data.result.mentorshipProgramGuide as MentoringProgramGuide;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const updateMentorshipProgramGuide = createAsyncThunk<MentoringProgramGuide, GuidesParams>(
  'UPDATE_MENTORSHIP_PROGRAM_GUIDE',
  async ({ mentoringProgramId, guideId, mentorshipProgramGuide }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.put(
        `/mentorship_programs/${mentoringProgramId}/guides/${guideId}.json`,
        {
          mentorshipProgramGuide,
        },
      );

      return response.data.result.mentorshipProgramGuide as MentoringProgramGuide;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const deleteMentorshipProgramGuide = createAsyncThunk<boolean, GuidesParams>(
  'DELETE_MENTORSHIP_PROGRAM_GUIDE',
  async ({ mentoringProgramId, guideId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `/mentorship_programs/${mentoringProgramId}/guides/${guideId}.json`,
      );

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));

      return rejectWithValue(error);
    }
  },
);
